import React, { useState, useEffect } from 'react';
import { useProfile } from '../../contexts/ProfileContext';
import { authFetch } from '../../utils/auth';
import { exportAllUserData } from '../../utils/dataExport';
import { getEmailFromClaims } from '../../utils/claims';
import { useMsal } from "@azure/msal-react";
import { getConsent, onConsentChange, openConsentBanner } from '../../utils/consent';

const consentStatusLabel = (consent) => {
  if (consent?.choice === 'accepted') return 'Du har godtatt informasjonskapsler til besøksstatistikk.';
  if (consent?.choice === 'declined') return 'Du har avslått informasjonskapsler til besøksstatistikk.';
  return 'Du har ikke tatt et valg om informasjonskapsler ennå.';
};

export default function AccountSettings() {
  const { loaded } = useProfile();
  const { instance, accounts } = useMsal();
  const [email, setEmail] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Outcome of the export: { type: 'error' } (nothing downloaded) or
  // { type: 'partial', failedSections } (file downloaded, sections missing).
  // Rendered inline under the export button — no alert(), no silent failure.
  const [exportNotice, setExportNotice] = useState(null);
  const [consent, setConsentState] = useState(() => getConsent());

  // Newsletter (marketing) consent — backed by GET/PUT /api/consents, saved
  // immediately on toggle. 'loading' → initial fetch, 'saving' → PUT in
  // flight (toggle disabled), 'unavailable' → the fetch failed.
  const [newsletterGranted, setNewsletterGranted] = useState(false);
  const [newsletterState, setNewsletterState] = useState('loading');
  const [newsletterError, setNewsletterError] = useState(null); // null | 'email_missing' | 'generic'

  // Keep the status line current when the user re-chooses via the banner
  useEffect(() => onConsentChange(setConsentState), []);

  useEffect(() => {
    if (loaded) {
      // Email comes from the Vipps-sourced ID token claims — under Vipps,
      // account.username may be a GUID, never show it as an email. Empty
      // string means no email claim; the field is hidden entirely then.
      setEmail(getEmailFromClaims(instance.getActiveAccount() || accounts[0]) || '');
    }
  }, [loaded, accounts, instance]);

  // Load the stored consent state once. A missing record means the user
  // never made a choice — shown as off.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await authFetch('/consents');
        const body = await response.json();
        if (cancelled) return;
        setNewsletterGranted(body?.consents?.marketing_email?.status === 'granted');
        setNewsletterState('ready');
      } catch (error) {
        console.error('Error loading consents:', error);
        if (!cancelled) setNewsletterState('unavailable');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleNewsletter = async () => {
    if (newsletterState !== 'ready') return;
    setNewsletterState('saving');
    setNewsletterError(null);
    try {
      const response = await authFetch('/consents/marketing_email', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ granted: !newsletterGranted })
      });
      const body = await response.json();
      setNewsletterGranted(body?.consent?.status === 'granted');
    } catch (error) {
      console.error('Error saving newsletter consent:', error);
      // 409 = the Profile has no email (the Vipps login shared none) — the
      // backend refuses the grant because there is no address to send to.
      setNewsletterError(error?.status === 409 ? 'email_missing' : 'generic');
    } finally {
      setNewsletterState('ready');
    }
  };

  // Full GDPR export of all seven stores via the shared utility (same
  // endpoint, filename and failure handling as the Coord minner page).
  const exportData = async () => {
    if (exporting) return;
    setExporting(true);
    setExportNotice(null);
    const result = await exportAllUserData();
    setExporting(false);
    if (result.status === 'error') {
      setExportNotice({ type: 'error' });
    } else if (result.status === 'partial') {
      setExportNotice({ type: 'partial', failedSections: result.failedSections });
    }
  };

  const deleteAllData = async () => {
    if (deleting) return;

    if (!window.confirm('Er du sikker på at du vil slette alle dine data? Dette kan ikke angres.')) {
      return;
    }

    if (!window.confirm('ADVARSEL: Dette vil slette ALL din data permanent. Skriv "SLETT" i neste dialogboks for å bekrefte.')) {
      return;
    }

    const confirmation = window.prompt('Skriv "SLETT" for å bekrefte permanent sletting av alle data:');
    if (confirmation !== 'SLETT') {
      alert('Sletting avbrutt.');
      return;
    }

    // Pending state from the moment the request fires — erasure can take
    // 10+ seconds under retry, and the button must not allow a second
    // submission meanwhile.
    setDeleting(true);

    try {
      // One orchestrated erasure call. The backend deletes every store
      // (minner, samtaler, samlinger, søknadsutkast, utkast, bruksdata,
      // abonnement, samtykker, e-postkontakten hos Brevo, profil), stops a
      // live Vipps-betalingsavtale, and reports per-store outcomes — it
      // never returns a bare success on partial failure, so the UI can be
      // honest.
      const response = await authFetch('/account/data', { method: 'DELETE' });

      let body = null;
      try {
        body = await response.json();
      } catch (parseError) {
        body = null;
      }

      if (response.ok && body && body.success) {
        // Every store confirmed deleted. The session must not survive the
        // account's data: log the user fully out via MSAL. window.alert
        // blocks until dismissed, so the confirmation is seen before the
        // redirect fires. (Matches this file's existing window.alert/confirm
        // pattern — there is no toast system in the app.)
        // `deleting` intentionally stays true — the button must not
        // re-enable in the moment between the alert and the redirect.
        window.alert('Alle dine data er nå slettet. Du blir logget ut.');
        await instance.logoutRedirect();
        return;
      }

      // Partial failure: name exactly what was NOT deleted, and do NOT claim
      // success, do NOT log out or redirect to onboarding.
      const failed = (body && Array.isArray(body.results))
        ? body.results.filter(r => !r.ok)
        : [];

      if (failed.length > 0) {
        const storeLabels = {
          memories: 'minner',
          conversations: 'samtaler',
          collections: 'samlinger',
          soknadDrafts: 'søknadsutkast',
          drafts: 'utkast',
          tokenUsage: 'bruksdata',
          subscription: 'abonnement',
          vippsAgreement: 'betalingsavtalen hos Vipps',
          consents: 'samtykker',
          brevoContact: 'e-postkontakten hos utsendingstjenesten vår',
          profile: 'profil'
        };
        const failedNames = failed
          .map(f => storeLabels[f.store] || f.store)
          .join(', ');
        window.alert(
          `Vi fikk dessverre ikke slettet alt. Dette ble ikke slettet: ${failedNames}. ` +
          'Prøv igjen om et øyeblikk – allerede slettede data forblir slettet. ' +
          'Hvis det fortsatt ikke går, ta kontakt med support, så hjelper vi deg med å fullføre slettingen.'
        );
      } else {
        window.alert('Noe gikk galt – dataene dine ble ikke slettet. Prøv igjen.');
      }
      setDeleting(false);
    } catch (error) {
      console.error('Error deleting data:', error);
      alert('Noe gikk galt under slettingen. Vi kan ikke bekrefte at noe ble slettet. Prøv igjen.');
      setDeleting(false);
    }
  };

  if (!loaded) {
    return <div className="text-center p-8">Laster innstillinger...</div>;
  }

  return (
    <div className="max-w-2xl">
      <h2 className="text-xl font-semibold mb-6">Kontoinnstillinger</h2>
      <p className="text-gray-600 mb-8">
        Administrer dine kontoinnstillinger, personvern og notifikasjoner.
      </p>

      <div className="space-y-8">
        {/* Account Information */}
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-medium mb-4">Kontoinformasjon</h3>
          
          <div className="space-y-4">
            {email ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-postadresse
                </label>
                <input
                  type="email"
                  value={email}
                  disabled
                  className="w-full p-3 border rounded-lg bg-gray-50 text-gray-500"
                />
                <p className="text-sm text-gray-500 mt-1">
                  E-postadressen er hentet fra Vipps og kan ikke endres her.
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Kontoen din er knyttet til din Vipps-innlogging.
              </p>
            )}
          </div>
        </div>

        {/* Notification Preferences — honest: names only email we actually
            send. Service email is informational (can't be disabled); the
            newsletter toggle is real marketing consent, saved immediately
            via PUT /api/consents/marketing_email. */}
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-medium mb-4">Notifikasjonsinnstillinger</h3>

          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Kvitteringer og viktige beskjeder om kontoen din sendes alltid
              til e-postadressen din. Disse kan ikke skrus av.
            </p>

            <div className="flex items-center justify-between pt-4 border-t">
              <div className="pr-4">
                <label className="text-sm font-medium text-gray-700">
                  Nyheter og tips fra Coord
                </label>
                <p className="text-sm text-gray-500">
                  Få nyheter og nyttige tips om støtteordninger og rettigheter
                  på e-post – du kan når som helst melde deg av.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={newsletterGranted}
                  onChange={toggleNewsletter}
                  disabled={newsletterState !== 'ready'}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sage-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sage peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
              </label>
            </div>

            {newsletterState === 'saving' && (
              <p className="text-sm text-text-muted">Lagrer valget ditt …</p>
            )}
            {newsletterState === 'unavailable' && (
              <p className="text-sm text-error-700">
                Vi fikk ikke hentet innstillingene dine. Last siden på nytt
                for å prøve igjen.
              </p>
            )}
            {newsletterError === 'email_missing' && (
              <p className="text-sm text-error-700">
                E-postadresse mangler fra innloggingen din. For å motta
                nyhetsbrev må du logge inn med Vipps på nytt og dele
                e-postadressen din.
              </p>
            )}
            {newsletterError === 'generic' && (
              <p className="text-sm text-error-700">
                Vi fikk ikke lagret valget ditt. Prøv igjen om et øyeblikk.
              </p>
            )}
          </div>
        </div>

        {/* Cookie consent */}
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-medium mb-4">Informasjonskapsler</h3>
          <p className="text-sm text-gray-500 mb-3">
            {consentStatusLabel(consent)} Du kan når som helst endre valget ditt.
          </p>
          <button
            onClick={openConsentBanner}
            className="px-4 py-2 min-h-[48px] border border-border-dark text-text-primary rounded-lg hover:bg-background-sidebar-active text-sm transition-colors"
          >
            Endre valg for informasjonskapsler
          </button>
        </div>

        {/* Data Management */}
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-medium mb-4">Datahåndtering</h3>
          
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Eksporter dine data
              </h4>
              <p className="text-sm text-gray-500 mb-3">
                Last ned en kopi av alle dine lagrede data i JSON-format –
                profilen din, samtaler, samlinger, søknadsutkast, minner,
                bruksstatistikk, abonnement og samtykker.
              </p>
              <button
                onClick={exportData}
                disabled={exporting}
                className="px-4 py-2 min-h-[48px] bg-primary text-white rounded-lg hover:bg-primary-600 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exporting ? 'Eksporterer …' : 'Eksporter data'}
              </button>
              {/* Export failure notice — inline near the button, never silent */}
              {exportNotice && (
                <div className="text-sm text-error-700 mt-2">
                  {exportNotice.type === 'error' ? (
                    <p>Kunne ikke eksportere dataene dine. Prøv igjen senere.</p>
                  ) : (
                    <>
                      <p>Filen ble lastet ned, men deler av eksporten feilet og mangler i filen:</p>
                      <ul className="list-disc ml-5 mt-1 space-y-1">
                        {exportNotice.failedSections.map((section, i) => (
                          <li key={i}>{section}</li>
                        ))}
                      </ul>
                      <p className="mt-1">Prøv igjen senere for å få en komplett eksport.</p>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="pt-4 border-t">
              <h4 className="text-sm font-medium text-error-700 mb-2">
                Slett alle data
              </h4>
              <p className="text-sm text-text-muted mb-3">
                Permanent slett alle dine data inkludert minner, samtaler og profil.
                Dette kan ikke angres.
              </p>
              <button
                onClick={deleteAllData}
                disabled={deleting}
                className="px-4 py-2 bg-error text-white rounded-lg hover:bg-error-600 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Sletter alle data …
                  </>
                ) : (
                  'Slett alle data'
                )}
              </button>
              {deleting && (
                <p className="text-sm text-text-muted mt-2">
                  Dette kan ta litt tid. Ikke lukk siden.
                </p>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}